import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { HierarchyApi, type Branch, type Constituency, type District, type Ward } from "../../admin/hierarchy/api";
import { ErrorPanel } from "../../shell/ErrorPanel";
import {
  METHODOLOGY_LABELS,
  VoterIntelApi,
  VotersApi,
  type VoterIntelConfidence,
  type VoterIntelMethodology,
} from "./api";

/**
 * Voter intel capture — sample-size driven.
 *
 * Officers can't poll an entire ward. Instead they record what a *sample*
 * of voters told them: "I spoke to 100 people. 67 said they're with us, 22
 * with the opposition, 11 undecided." We:
 *   1. Validate parts ≤ sampleSize
 *   2. Compute percentages
 *   3. Project onto the scope's registered population (from voterCounts)
 *   4. Surface a low-N warning when sampleSize < 30 (rule of thumb;
 *      smaller samples are unreliable for population estimates)
 *   5. Submit both the raw sample AND the projected population estimates
 */
export function VoterIntelCapture() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const [scopeLevel, setScopeLevel] = useState<"district" | "constituency" | "ward" | "branch">("ward");
  const [did, setDid] = useState("");
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const [period, setPeriod] = useState(String(new Date().getFullYear()));
  const [methodology, setMethodology] = useState<VoterIntelMethodology>("door_to_door");
  const [confidence, setConfidence] = useState<VoterIntelConfidence>("medium");
  const [sampleSize, setSampleSize] = useState("100");
  const [sampleOurs, setSampleOurs] = useState("0");
  const [sampleOpposition, setSampleOpposition] = useState("0");
  const [sampleUndecided, setSampleUndecided] = useState("0");
  const [reasons, setReasons] = useState("");
  const [threats, setThreats] = useState("");
  const [classification, setClassification] = useState<"unclassified" | "restricted" | "secret">("restricted");
  // Intel quality metadata — sources counted (independent of sample size,
  // which is *interactions*; sources are *people who told you the bigger
  // picture*) and a 0–100 numeric confidence score. Both optional.
  const [sourcesCount, setSourcesCount] = useState("");
  const [confidenceScore, setConfidenceScore] = useState("");
  const [error, setError] = useState<unknown>(null);

  // Cascading hierarchy data
  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });
  const constituencies = useQuery({
    queryKey: ["constituencies", did],
    queryFn: () => HierarchyApi.listConstituenciesInDistrict(did),
    enabled: !!did,
  });
  const wards = useQuery({
    queryKey: ["wards", cid],
    queryFn: () => HierarchyApi.listWards(cid),
    enabled: !!cid,
  });
  const branches = useQuery({
    queryKey: ["branches", wid],
    queryFn: () => HierarchyApi.listBranches(wid),
    enabled: !!wid,
  });

  const scopeId =
    scopeLevel === "branch" ? bid :
    scopeLevel === "ward" ? wid :
    scopeLevel === "constituency" ? cid : did;

  // Fetch the matching voter count so we can project sample percentages onto
  // the registered total. If no count exists yet we surface a hint.
  const counts = useQuery({
    queryKey: ["voter-count-for-scope", scopeLevel, scopeId, period],
    queryFn: () => VotersApi.list({ scopeLevel, scopeId, period }),
    enabled: !!scopeId && !!period,
  });
  const registered = counts.data?.items?.[0]?.registered ?? 0;

  // Reset child selections when parent changes.
  useEffect(() => { setCid(""); setWid(""); setBid(""); }, [did]);
  useEffect(() => { setWid(""); setBid(""); }, [cid]);
  useEffect(() => { setBid(""); }, [wid]);

  const projection = useMemo(() => {
    const n = Math.max(0, Number(sampleSize) || 0);
    const o = Math.max(0, Number(sampleOurs) || 0);
    const op = Math.max(0, Number(sampleOpposition) || 0);
    const u = Math.max(0, Number(sampleUndecided) || 0);
    const used = o + op + u;
    const validSample = n > 0 && used <= n;
    const pctOurs = validSample ? o / n : 0;
    const pctOpp = validSample ? op / n : 0;
    const pctUnd = validSample ? u / n : 0;
    return {
      n, o, op, u, used,
      validSample,
      pctOurs, pctOpp, pctUnd,
      // Low-N warning: <30 is too small for any meaningful projection.
      // <100 is shaky for a constituency-level claim.
      lowN: n > 0 && n < 30,
      mediumN: n >= 30 && n < 100,
      // Project onto registered population. If no registered count is
      // known yet, fall back to the sample numbers themselves so the
      // record still has *some* estimate.
      projectedOurs: registered > 0 && validSample ? Math.round(pctOurs * registered) : o,
      projectedOpp: registered > 0 && validSample ? Math.round(pctOpp * registered) : op,
      projectedUnd: registered > 0 && validSample ? Math.round(pctUnd * registered) : u,
    };
  }, [sampleSize, sampleOurs, sampleOpposition, sampleUndecided, registered]);

  const create = useMutation({
    mutationFn: () => VoterIntelApi.create({
      scopeLevel,
      scopeId,
      period,
      sampleSize: projection.n,
      sampleOurs: projection.o,
      sampleOpposition: projection.op,
      sampleUndecided: projection.u,
      methodology,
      confidence,
      estimatedOurs: projection.projectedOurs,
      estimatedOpposition: projection.projectedOpp,
      estimatedUndecided: projection.projectedUnd,
      reasons,
      threats,
      classification,
      sourcesCount: sourcesCount.trim() === "" ? undefined : Number(sourcesCount),
      confidenceScore: confidenceScore.trim() === "" ? undefined : Number(confidenceScore),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["voter-intel"] });
      nav("/app/voters");
    },
    onError: (e) => setError(e),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!scopeId) { setError(new Error("Pick a scope to record against.")); return; }
    if (!reasons.trim()) { setError(new Error("Reasons / observations are required — explain why you believe these numbers.")); return; }
    if (!projection.validSample) {
      setError(new Error("Sample parts (ours + opposition + undecided) cannot exceed sampleSize."));
      return;
    }
    create.mutate();
  }

  return (
    <div>
      <Link to=".." className="muted" style={{ fontSize: 13 }}>← Back to voters</Link>
      <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 600 }}>Capture voter intel</h1>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 14 }}>
        Tell us what a sample of voters said. We'll compute percentages and project them onto
        the registered total in this scope.
      </p>

      <ErrorPanel err={error} title="Couldn't capture intel" />

      <form onSubmit={submit} className="card" style={{ padding: 16 }}>
        <div className="row-3" style={{ gap: 8 }}>
          <Field label="Scope level">
            <select value={scopeLevel} onChange={(e) => setScopeLevel(e.target.value as typeof scopeLevel)}>
              <option value="district">District</option>
              <option value="constituency">Constituency</option>
              <option value="ward">Ward</option>
              <option value="branch">Branch</option>
            </select>
          </Field>
          <Field label="Period">
            <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g. 2026" />
          </Field>
          <Field label="Methodology">
            <select value={methodology} onChange={(e) => setMethodology(e.target.value as VoterIntelMethodology)}>
              {(Object.keys(METHODOLOGY_LABELS) as VoterIntelMethodology[]).map((m) => (
                <option key={m} value={m}>{METHODOLOGY_LABELS[m]}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="row-3" style={{ gap: 8, marginTop: 8 }}>
          <Field label="District">
            <select value={did} onChange={(e) => setDid(e.target.value)}>
              <option value="">— Select —</option>
              {districts.data?.items.map((d: District) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          {scopeLevel !== "district" && (
            <Field label="Constituency">
              <select value={cid} onChange={(e) => setCid(e.target.value)} disabled={!did}>
                <option value="">— Select —</option>
                {constituencies.data?.items.map((c: Constituency) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          {(scopeLevel === "ward" || scopeLevel === "branch") && (
            <Field label="Ward">
              <select value={wid} onChange={(e) => setWid(e.target.value)} disabled={!cid}>
                <option value="">— Select —</option>
                {wards.data?.items.map((w: Ward) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
          )}
        </div>

        {scopeLevel === "branch" && (
          <div className="row-3" style={{ gap: 8, marginTop: 8 }}>
            <Field label="Branch">
              <select value={bid} onChange={(e) => setBid(e.target.value)} disabled={!wid}>
                <option value="">— Select —</option>
                {branches.data?.items.map((b: Branch) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <span /><span />
          </div>
        )}

        <div style={{ marginTop: 16, padding: 12, background: "var(--bg-muted, #f7f7f9)", borderRadius: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Sample data</div>
          <div className="row-3" style={{ gap: 8 }}>
            <Field label="People interacted with (sample size)">
              <input type="number" min="0" value={sampleSize} onChange={(e) => setSampleSize(e.target.value)} />
            </Field>
            <Field label="Said they're ours">
              <input type="number" min="0" value={sampleOurs} onChange={(e) => setSampleOurs(e.target.value)} />
            </Field>
            <Field label="Said opposition">
              <input type="number" min="0" value={sampleOpposition} onChange={(e) => setSampleOpposition(e.target.value)} />
            </Field>
          </div>
          <div className="row-3" style={{ gap: 8, marginTop: 8 }}>
            <Field label="Undecided">
              <input type="number" min="0" value={sampleUndecided} onChange={(e) => setSampleUndecided(e.target.value)} />
            </Field>
            <span /><span />
          </div>

          {projection.n > 0 && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              <strong>From this sample:</strong>{" "}
              {(projection.pctOurs * 100).toFixed(1)}% ours · {(projection.pctOpp * 100).toFixed(1)}% opposition · {(projection.pctUnd * 100).toFixed(1)}% undecided
              {projection.used < projection.n && (
                <span className="muted"> · {projection.n - projection.used} unaccounted</span>
              )}
            </div>
          )}

          {(projection.lowN || projection.mediumN) && (
            <div
              role="alert"
              style={{
                marginTop: 8, fontSize: 12, padding: 8, borderRadius: 4,
                background: projection.lowN ? "var(--danger-soft, #fee2e2)" : "var(--warn-soft, #fef3c7)",
                color: projection.lowN ? "var(--danger)" : "var(--warn, #92400e)",
              }}
            >
              {projection.lowN
                ? `Sample of ${projection.n} is too small for a reliable projection. Treat the population numbers as a rough indicator only.`
                : `Sample of ${projection.n} gives an OK directional estimate but not statistically tight. Consider expanding before reporting upward.`}
            </div>
          )}

          {projection.n > 0 && registered > 0 && projection.validSample && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <strong>Projected to {registered.toLocaleString()} registered voters in this scope:</strong>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                ≈ {projection.projectedOurs.toLocaleString()} ours · {projection.projectedOpp.toLocaleString()} opposition · {projection.projectedUnd.toLocaleString()} undecided
              </div>
            </div>
          )}
          {projection.n > 0 && registered === 0 && (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              No registered-voter count is on file for this scope/period yet. The intel will store
              the raw sample numbers as the population estimate; capture a count first if you want
              proper projection.
            </div>
          )}
        </div>

        <div className="row-3" style={{ gap: 8, marginTop: 16 }}>
          <Field label="Confidence (rough)">
            <select value={confidence} onChange={(e) => setConfidence(e.target.value as VoterIntelConfidence)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </Field>
          <Field label="Classification">
            <select value={classification} onChange={(e) => setClassification(e.target.value as typeof classification)}>
              <option value="unclassified">Unclassified</option>
              <option value="restricted">Restricted</option>
              <option value="secret">Secret</option>
            </select>
          </Field>
          <span />
        </div>

        <div style={{ marginTop: 8, padding: 12, background: "var(--bg-muted, #f7f7f9)", borderRadius: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Intel quality</div>
          <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
            Sources = how many separate informants/sources contributed to your overall picture (different from sample size, which is people you interacted with). Confidence score is a precise 0–100 number.
          </p>
          <div className="row-3" style={{ gap: 8 }}>
            <Field label="Sources">
              <input type="number" min={0} value={sourcesCount} onChange={(e) => setSourcesCount(e.target.value)} placeholder="e.g. 4" />
            </Field>
            <Field label="Confidence (0–100)">
              <input type="number" min={0} max={100} value={confidenceScore} onChange={(e) => setConfidenceScore(e.target.value)} placeholder="e.g. 70" />
            </Field>
            <span />
          </div>
        </div>

        <Field label="Reasons / observations">
          <textarea
            value={reasons}
            onChange={(e) => setReasons(e.target.value)}
            placeholder="Why do you believe these numbers? What did people say? Encrypted at rest."
            rows={3}
            style={{ width: "100%" }}
          />
        </Field>
        <Field label="Threats noticed (optional)">
          <textarea
            value={threats}
            onChange={(e) => setThreats(e.target.value)}
            placeholder="Anything threatening these numbers — opposition activity, community grievances, etc. Encrypted at rest."
            rows={2}
            style={{ width: "100%" }}
          />
        </Field>

        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Save intel"}
          </button>
          <Link to=".." className="ghost" style={{ padding: "6px 12px" }}>Cancel</Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 8 }}>
      <label style={{ display: "block", fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{label}</label>
      {children}
    </div>
  );
}
